import { parse } from "date-fns"
import { kebabCase } from "lodash"
import { z } from "zod"

const literalSchema = z.union([z.string(), z.number(), z.boolean(), z.null()])

export type Literal = z.infer<typeof literalSchema>

export type Json = Literal | { [key: string]: Json } | Json[]
export type JsonObject = { [key: string]: Json }

export const jsonSchema: z.ZodType<Json> = z.lazy(() =>
  z.union([literalSchema, z.array(jsonSchema), z.record(jsonSchema)])
)

export const json = () => jsonSchema

export const jsonObjectSchema: z.ZodType<JsonObject> = z.lazy(() =>
  z.record(jsonObjectSchema)
)

export const jsonObject = () => jsonObjectSchema

export const stringToJSONObjectSchema = z
  .string()
  .transform((str, ctx): z.infer<ReturnType<typeof jsonObject>> => {
    try {
      return jsonObjectSchema.parse(JSON.parse(str))
    } catch (_) {
      ctx.addIssue({ code: "custom", message: "Invalid JSON" })
      return z.NEVER
    }
  })

export const stringToJSONSchema = z
  .string()
  .transform((str, ctx): z.infer<ReturnType<typeof json>> => {
    try {
      return jsonSchema.parse(JSON.parse(str))
    } catch (_) {
      ctx.addIssue({ code: "custom", message: "Invalid JSON" })
      return z.NEVER
    }
  })

export const stringToDateSchema = z.string().transform((date, ctx) => {
  const newDate = parse(date, "dd/M/yyyy", new Date())
  if (newDate.toString() === "Invalid Date") {
    ctx.addIssue({ code: "custom", message: "Invalid date" })
    return z.NEVER
  }
  return newDate
})

export const kebabCaseSchema = z.string().transform(kebabCase)

export const isDigitsSchema = z.preprocess((x) => Number(x), z.number())

export const standardString = (): z.ZodString => z.string().trim()

export const standardStringOptional = (): z.ZodOptional<z.ZodString> =>
  z.string().trim().optional()

export const standardEmail = (): z.ZodString =>
  z
    .string({ required_error: "Email is required" })
    .toLowerCase()
    .trim()
    .regex(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i, {
      message: "Invalid email format",
    })

export const uniqueStringArraySchema = z
  .array(standardString())
  .transform((val) => Array.from(new Set(val)))

export type AnyFunction = (...args: any[]) => any // eslint-disable-line @typescript-eslint/no-explicit-any -- it could trully be anything

/**
 * Remove undefined values from an object recursively
 * @param obj - The object to remove undefined values from
 * @returns The object with undefined members removed
 * @example
 * ```ts
 * removeUndefined({ a: undefined, b: { c: undefined, d: 1 } })
 * // { b: { d: 1 } }
 * ```
 */
export function removeUndefined<T>(obj: PartialOrUndefined<T>): {
  [K in keyof T as undefined extends T[K] ? never : K]: T[K] extends Function // Function is the correct type here
    ? T[K]
    : T[K] extends object
      ? ReturnType<typeof removeUndefined<T[K]>>
      : Exclude<T[K], undefined>
} {
  const entries = Object.entries(obj)
    // Filter undefined values
    .filter(([, value]) => value !== undefined)
    // Recursively remove undefined values from nested objects
    .map(([key, value]) => {
      if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        typeof value !== "function" &&
        !(value instanceof Date)
      ) {
        return [key, removeUndefined(value)]
      }
      return [key, value]
    }) as [
    keyof T,
    T[keyof T] extends object
      ? ReturnType<typeof removeUndefined<T[keyof T]>>
      : Exclude<T[keyof T], undefined>,
  ][]
  return Object.fromEntries(entries) as any // eslint-disable-line @typescript-eslint/no-explicit-any -- will resolve to the return-type in the type signature
}

/**
 * Remove null values from an object recursively, preserving optional properties
 * @param obj - The object to remove null values from
 * @returns The object with null members removed, preserving optional properties
 * @example
 * ```ts
 * removeNullValues({ a: null, b: { c: null, d: 1 } })
 * // { b: { d: 1 } }
 * ```
 */
export function removeNullValues<T extends object>(
  obj: T
): {
  [K in keyof T as null extends T[K]
    ? T[K] extends null
      ? never
      : K
    : K]?: T[K] extends Function // Function is the correct type here
    ? T[K]
    : T[K] extends object
      ? ReturnType<typeof removeNullValues<T[K]>>
      : NonNullable<T[K]>
} {
  const entries = Object.entries(obj)
    .filter(([, value]) => value !== null)
    .map(([key, value]) => {
      if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        typeof value !== "function" &&
        !(value instanceof Date)
      ) {
        return [key, removeNullValues(value)]
      }
      return [key, value]
    }) as [
    keyof T,
    T[keyof T] extends object
      ? ReturnType<typeof removeNullValues<T[keyof T]>>
      : NonNullable<T[keyof T]>,
  ][]
  return Object.fromEntries(entries) as any // eslint-disable-line @typescript-eslint/no-explicit-any -- will resolve to the return-type in the type signature
}

export type PartialOrUndefined<T> = { [P in keyof T]?: T[P] | undefined }

export type NotUndefined<T> = T extends undefined ? never : T

export type RequiredAndDefined<T> = {
  [P in keyof T]-?: NotUndefined<T[P]>
}

export const PaginationSchema = z.object({
  pagination: z
    .object({
      page: z.coerce.number(),
      pageSize: z.coerce.number().positive(),
    })
    .optional(),
  searchParams: z.string().optional(),
})
export type PaginationType = z.infer<typeof PaginationSchema>

export const isValidJson = <T>(
  param: string,
  schema: z.ZodType<T>
): boolean => {
  try {
    const parsed = JSON.parse(param)
    return schema.safeParse(parsed).success
  } catch {
    return false
  }
}

export const parseSafeJson = <T>(
  param: string,
  schema: z.ZodType<T>
): T | undefined => {
  try {
    const parsed = JSON.parse(param)
    return schema.parse(parsed)
  } catch {
    return undefined
  }
}

export function deepMerge<T>(base: T, override: Partial<T>): T {
  const result: T = { ...base }

  for (const key in override) {
    const k = key as keyof T
    const overrideValue = override[k]

    if (
      overrideValue &&
      typeof overrideValue === "object" &&
      !Array.isArray(overrideValue)
    ) {
      const baseValue = base[k]
      result[k] = deepMerge(
        (baseValue ?? {}) as T[keyof T],
        overrideValue as Partial<T[keyof T]>
      )
    } else {
      result[k] = overrideValue as T[keyof T]
    }
  }

  return result
}

export function getSchemaKeysAsNull(
  schema: z.ZodObject<any> | z.ZodEffects<any>
): Record<string, null> {
  let actualSchema = schema
  if (schema instanceof z.ZodEffects) {
    actualSchema = schema._def.schema
  }

  if (actualSchema instanceof z.ZodObject) {
    const shape = actualSchema.shape
    const skeleton: Record<string, null> = {}

    for (const key in shape) {
      skeleton[key] = null
    }

    return skeleton
  }

  return {}
}
